import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MapPin, Search, Crosshair } from "lucide-react";

const ICON = L.divIcon({
  className: "mb-picker-marker",
  html: '<div class="mb-pin platinum" style="width:48px;height:48px"><span style="color:white;font-weight:bold">📍</span></div>',
  iconSize: [48, 48],
  iconAnchor: [24, 52],
});

function ClickHandler({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng); } });
  return null;
}

function FlyTo({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, Math.max(map.getZoom(), 16), { duration: 1 }); }, [center, map]);
  return null;
}

/** Reusable map picker. Pass `value={{lat, lng}}` and `onChange({lat, lng, address?})`. */
export default function LocationPicker({ value, onChange, label = "Selecionar localização", buttonClass = "" }) {
  const [open, setOpen] = useState(false);
  const initial = (value && Number.isFinite(value.lat) && Number.isFinite(value.lng)) ? value : { lat: -10.85, lng: -37.12 };
  const [pos, setPos] = useState(initial);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [flyCenter, setFlyCenter] = useState(null);
  const markerRef = useRef(null);

  useEffect(() => { if (value && Number.isFinite(value.lat) && Number.isFinite(value.lng)) setPos(value); }, [value]);

  const dragEnd = () => {
    const m = markerRef.current;
    if (m) {
      const ll = m.getLatLng();
      setPos({ lat: ll.lat, lng: ll.lng });
    }
  };

  const searchNominatim = async (e) => {
    e?.preventDefault?.();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(query)}&countrycodes=br`);
      const list = await r.json();
      if (list && list[0]) {
        const lat = parseFloat(list[0].lat); const lng = parseFloat(list[0].lon);
        setPos({ lat, lng, address: list[0].display_name });
        setFlyCenter([lat, lng]);
      }
    } catch { /* swallow */ }
    finally { setSearching(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((p) => {
      const lat = p.coords.latitude, lng = p.coords.longitude;
      setPos({ lat, lng });
      setFlyCenter([lat, lng]);
    });
  };

  const confirm = () => {
    onChange({ lat: pos.lat, lng: pos.lng, address: pos.address });
    setOpen(false);
  };

  const hasCoords = Number.isFinite(value?.lat) && Number.isFinite(value?.lng) && (value.lat !== 0 || value.lng !== 0);

  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)} className={`rounded-xl ${buttonClass}`} data-testid="open-location-picker">
        <MapPin size={14} className="mr-1.5"/>
        {hasCoords ? `Lat ${value.lat.toFixed(4)}, Lng ${value.lng.toFixed(4)} — Mudar no mapa` : label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden p-0 rounded-2xl" data-testid="location-picker-dialog">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="font-display text-2xl">Onde fica sua empresa?</DialogTitle>
            <p className="text-xs text-stone-500 mt-1">Pesquise o endereço, mova o pin ou clique no mapa para escolher o local exato.</p>
          </DialogHeader>

          <form onSubmit={searchNominatim} className="px-5 pt-3 pb-2 flex gap-2 items-center">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar endereço (Rua, nº, cidade, estado)..." className="flex-1 rounded-xl" data-testid="picker-search-input"/>
            <Button type="submit" disabled={searching} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="picker-search-btn">
              <Search size={14} className="mr-1.5"/> {searching ? "Buscando..." : "Buscar"}
            </Button>
            <Button type="button" variant="outline" onClick={useMyLocation} className="rounded-xl" data-testid="picker-my-location">
              <Crosshair size={14} className="mr-1.5"/> Minha localização
            </Button>
          </form>

          <div className="h-[55vh] mx-5 mb-2 rounded-xl overflow-hidden border-2 border-stone-200">
            <MapContainer center={[initial.lat, initial.lng]} zoom={15} style={{ width: "100%", height: "100%" }} scrollWheelZoom>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
              <Marker
                ref={markerRef}
                position={[pos.lat, pos.lng]}
                draggable
                eventHandlers={{ dragend: dragEnd }}
                icon={ICON}
              />
              <ClickHandler onPick={(ll) => setPos({ lat: ll.lat, lng: ll.lng })}/>
              <FlyTo center={flyCenter}/>
            </MapContainer>
          </div>

          <div className="px-5 pb-5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-stone-600 font-mono">
              📍 <strong>Lat:</strong> {pos.lat.toFixed(6)} · <strong>Lng:</strong> {pos.lng.toFixed(6)}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancelar</Button>
              <Button type="button" onClick={confirm} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-bold" data-testid="picker-confirm">
                ✓ Usar esta localização
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
